import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { RootState } from "../../store";
import { login } from "../../store/slices/authSlice";
import { Eye, EyeOff, Loader } from "lucide-react";
import { useTranslation } from "react-i18next";

const VITRINE_URL = "https://www.affluxcorp.com";

// Charte afflux : bleu #1E3A8A (primary-600), vert #10B981 (accent-500), gris #F3F4F6
const inputClass =
  "block w-full h-11 px-3.5 rounded-lg border border-gray-300 bg-white text-[15px] text-gray-900 " +
  "placeholder:text-gray-400 " +
  "focus:outline-none focus:border-primary-600 focus:ring-[3px] focus:ring-primary-600/15 " +
  "transition-[border-color,box-shadow] duration-150";

export const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  const { isLoading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(login({ email, password }) as any);
  };

  const lang = i18n.language?.startsWith("en") ? "en" : "fr";

  return (
    <div className="min-h-screen flex flex-col bg-[#F3F4F6]">

      <div className="flex justify-end px-6 pt-5">
        <div className="flex items-center text-[13px] font-medium" role="group" aria-label="Langue">
          {(["fr", "en"] as const).map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => i18n.changeLanguage(code)}
              aria-pressed={lang === code}
              className={`px-2 py-1 rounded uppercase transition-colors ${
                lang === code ? "text-primary-600" : "text-gray-400 hover:text-gray-700"
              }`}
            >
              {code}
            </button>
          ))}
        </div>
      </div>

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <a href={VITRINE_URL} className="flex items-center gap-2.5 mb-8" aria-label="afflux">
          <img src="/logo.png" alt="" className="h-10 w-auto" />
          <span className="font-display text-[28px] font-bold text-primary-600 tracking-[-0.03em]">afflux</span>
        </a>

        <div className="w-full max-w-[420px] rounded-2xl bg-white border border-gray-200 shadow-[0_1px_3px_rgba(30,58,138,0.06),0_8px_24px_-8px_rgba(30,58,138,0.10)] px-6 py-8 sm:px-10 sm:py-10">
          <h1 className="font-display text-[24px] leading-tight font-semibold text-gray-900 tracking-[-0.02em]">
            {t("auth.login.title")}
          </h1>
          <p className="mt-1.5 text-[15px] text-gray-500">
            {t("auth.login.subtitle")}
          </p>

          {error && (
            <div
              role="alert"
              className="mt-6 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-800"
            >
              <span className="font-medium">{t("auth.login.error")}.</span> {error}
            </div>
          )}

          <form className="mt-7 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1.5">
                {t("auth.login.emailLabel")}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.login.emailPlaceholder")}
                className={inputClass}
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                  {t("auth.login.passwordLabel")}
                </label>
                <a
                  href="mailto:contact@affluxcorp.com"
                  className="text-[13px] font-medium text-primary-600 hover:text-primary-800 hover:underline underline-offset-2"
                >
                  {t("auth.login.forgotPassword")}
                </a>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? t("auth.login.hidePassword") : t("auth.login.showPassword")}
                  className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 inline-flex items-center justify-center rounded-lg bg-primary-600 text-[15px] font-semibold text-white
                         shadow-[0_1px_2px_rgba(30,58,138,0.20)] hover:bg-primary-700 active:bg-primary-800
                         focus:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-600/30
                         disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? <Loader className="h-4 w-4 animate-spin" aria-label={t("auth.login.submit")} /> : t("auth.login.submit")}
            </button>
          </form>
        </div>

        <p className="mt-6 text-sm text-gray-600">
          {t("auth.login.newHere")}{" "}
          <Link to="/register" className="font-semibold text-accent-700 hover:text-accent-800 hover:underline underline-offset-2">
            {t("auth.login.createAccount")}
          </Link>
        </p>
      </main>

      <footer className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 px-6 py-6 text-[13px] text-gray-500">
        <span>© {new Date().getFullYear()} Afflux</span>
        <a href={`${VITRINE_URL}/mentions-legales.html`} className="hover:text-gray-800">
          {t("auth.login.footerLegal")}
        </a>
        <a href={`${VITRINE_URL}/securite.html`} className="hover:text-gray-800">
          {t("auth.login.footerSecurity")}
        </a>
        <a href={`${VITRINE_URL}/contact.html`} className="hover:text-gray-800">
          {t("auth.login.footerContact")}
        </a>
      </footer>
    </div>
  );
};
